import { useEffect, useRef } from 'react';
import type { Graph, Edge } from '@antv/x6';
import { message } from 'antd';
import type { EdgeDialogInfo } from '../types';
import { safePageToLocal, getEventClientXY } from '../utils/iosCompat';
import {
  calculateObjectSize,
  calculateProcessSize,
  calculateStateSize,
  calculateValueSize,
  MIN_SIZES,
} from '../utils/sizing';
import { applyParallelEdgeOffset } from '../utils/edgeOffset';
import { mergeSameTypeEdges, applySingleEdgeRouting } from '../utils/mergeSameTypeEdges';
import { getStructuralEdgeShape } from '../edges/structural';

const DRAG_THRESHOLD = 5;
const PARENT_PAD = 10;
const OBJECT_HEADER_HEIGHT = 22;

const EDGE_VERTEX_TOOL = {
  name: 'vertices',
  args: {
    attrs: { fill: '#1890ff', stroke: '#fff', strokeWidth: 1, r: 5 },
    snapRadius: 10,
    addable: true,
    removable: true,
  },
};

const typeStrokeColors: Record<string, string> = {
  object: '#70E483',
  option: '#70E483',
  process: '#3BC3FF',
  state: '#fa8c16',
  value: '#4169E1',
};

const selectedStrokeColor = '#1890ff';

type ClipboardNode = {
  id: string;
  shape: string;
  data: Record<string, any>;
  position: { x: number; y: number };
  size: { width: number; height: number };
  children: Array<{
    id: string;
    shape: string;
    data: Record<string, any>;
    position: { x: number; y: number };
    size: { width: number; height: number };
  }>;
};

type ClipboardEdge = {
  id: string;
  shape: string;
  data: Record<string, any>;
  sourceId: string;
  targetId: string;
  sourcePort?: string;
  targetPort?: string;
  vertices?: { x: number; y: number }[];
  router?: any;
  connector?: any;
  attrs?: Record<string, any>;
};

type ClipboardPayload = {
  nodes: ClipboardNode[];
  edges: ClipboardEdge[];
};

function clampNodeToParent(
  node: any,
  parent: any,
  targetRelX: number,
  targetRelY: number,
): { x: number; y: number } {
  const parentType = parent.getData()?.type;
  const parentSize = parent.getSize();
  const nodeSize = node.getSize();

  if (parentType === 'process') {
    const centerX = parentSize.width / 2;
    const centerY = parentSize.height / 2;
    const rx = Math.max(0, centerX - PARENT_PAD - nodeSize.width / 2);
    const ry = Math.max(0, centerY - PARENT_PAD - nodeSize.height / 2);

    if (rx <= 0 || ry <= 0) {
      return { x: centerX - nodeSize.width / 2, y: centerY - nodeSize.height / 2 };
    }

    let relCenterX = targetRelX + nodeSize.width / 2;
    let relCenterY = targetRelY + nodeSize.height / 2;
    const dx = relCenterX - centerX;
    const dy = relCenterY - centerY;
    const distance = (dx * dx) / (rx * rx) + (dy * dy) / (ry * ry);

    if (distance > 1) {
      const scale = 1 / Math.sqrt(distance);
      relCenterX = centerX + dx * scale;
      relCenterY = centerY + dy * scale;
    }

    return { x: relCenterX - nodeSize.width / 2, y: relCenterY - nodeSize.height / 2 };
  }

  const minX = PARENT_PAD;
  const isObjectLike = parentType === 'object' || parentType === 'option';
  const minY = isObjectLike ? OBJECT_HEADER_HEIGHT + PARENT_PAD : PARENT_PAD;
  const maxX = Math.max(minX, parentSize.width - nodeSize.width - PARENT_PAD);
  const maxY = Math.max(minY, parentSize.height - nodeSize.height - PARENT_PAD);

  return {
    x: Math.min(Math.max(minX, targetRelX), maxX),
    y: Math.min(Math.max(minY, targetRelY), maxY),
  };
}

function applyNodeSelection(node: any, selected: boolean) {
  const type = node.getData()?.type || 'object';
  const originalColor = typeStrokeColors[type] || typeStrokeColors.object;
  const mode = node.getData()?.selectionMode;
  const shouldHighlight = selected && mode !== 'group';
  node.attr('body/stroke', shouldHighlight ? selectedStrokeColor : originalColor);
  node.setData({ selected });
}

export function useNodeInteractions(
  graphRef: React.MutableRefObject<Graph | null>,
  graph: Graph | null,
  options: {
    setStatusText: (text: string) => void;
    showEdgeDialog: (info: EdgeDialogInfo) => void;
    onNodeSelect?: (node: any) => void;
  }
) {
  const { setStatusText, showEdgeDialog, onNodeSelect } = options;

  // 通过 ref 保存最新的回调函数，确保画布事件闭包始终能访问到最新值
  const setStatusTextRef = useRef(setStatusText);
  setStatusTextRef.current = setStatusText;
  const showEdgeDialogRef = useRef(showEdgeDialog);
  showEdgeDialogRef.current = showEdgeDialog;
  const onNodeSelectRef = useRef(onNodeSelect);
  onNodeSelectRef.current = onNodeSelect;

  // 拖拽状态 — 全部使用 ref，因为画布事件回调需要获取最新值
  const dragSourceNodeRef = useRef<any>(null);
  const dragTempEdgeRef = useRef<Edge | null>(null);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const dragOriginalPosRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const isMovingNodeRef = useRef(false);
  const lastLocalPosRef = useRef({ x: 0, y: 0 });
  const highlightedTargetRef = useRef<any>(null);
  const pendingEdgeRef = useRef<Edge | null>(null);
  const selectionStartLocalRef = useRef<{ x: number; y: number } | null>(null);
  const selectionStartClientRef = useRef<{ x: number; y: number } | null>(null);
  const selectionCurrentLocalRef = useRef<{ x: number; y: number } | null>(null);
  const selectionCurrentClientRef = useRef<{ x: number; y: number } | null>(null);
  const selectionBoxRef = useRef<HTMLDivElement | null>(null);
  const selectionResultBoxRef = useRef<HTMLDivElement | null>(null);
  const selectionResultBoundsRef = useRef<
    { minX: number; minY: number; maxX: number; maxY: number } | null
  >(null);
  const isRubberbandActiveRef = useRef(false);
  const isRubberbandPreparingRef = useRef(false);
  const suppressBlankClickRef = useRef(false);
  const suppressNodeClickRef = useRef(false);
  const clipboardRef = useRef<ClipboardPayload | null>(null);
  const pasteCountRef = useRef(0);
  const lastCanvasClickRef = useRef<{ x: number; y: number } | null>(null);
  const clipboardUsedRef = useRef(false);
  const lastPastedNodeIdsRef = useRef<string[]>([]);
  const groupDragNodesRef = useRef<Array<{ node: any; originalPos: { x: number; y: number } }> | null>(null);
  const dragSourcePortRef = useRef<string | undefined>(undefined);
  const selectedEdgeRef = useRef<Edge | null>(null);
  const hoveredEdgeRef = useRef<Edge | null>(null);
  const edgeLeaveTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!graph || !graphRef.current) return;

    const createEdgeRemoveTool = () => ({
      name: 'button',
      args: {
        markup: [
          {
            tagName: 'circle',
            selector: 'button',
            attrs: {
              r: 9,
              stroke: '#ff4d4f',
              fill: '#fff',
              'stroke-width': 1.5,
              class: 'opm-edge-remove-btn-bg',
            },
          },
          {
            tagName: 'path',
            selector: 'icon',
            attrs: {
              d: 'M -3 -3 L 3 3 M -3 3 L 3 -3',
              stroke: '#ff4d4f',
              'stroke-width': 1.8,
              'stroke-linecap': 'round',
              class: 'opm-edge-remove-btn-text',
            },
          },
        ],
        distance: 0,
        offset: 0,
        onClick: ({ edge, view }: { edge?: Edge; view?: any }) => {
          const targetEdge: Edge | undefined =
            hoveredEdgeRef.current || selectedEdgeRef.current || edge || view?.cell;
          targetEdge?.remove?.();
        },
      },
    });

    const scheduleMerge = () => {
      if (!graph) return;
      mergeSameTypeEdges(graph);
    };

    const isStructuralEdge = (edge: Edge) => {
      const data = edge.getData?.() || {};
      return data.edgeCategory === 'structural';
    };

    const ensureConnectionPoint = (cfg: any) => {
      if (!cfg) return cfg;
      if (cfg.port === 'port-arrow') {
        cfg.connectionPoint = { name: 'anchor' };
      }
      return cfg;
    };

    type EdgeAnchorName = 'top' | 'bottom' | 'left' | 'right';
    const anchorForPort = (port: string): EdgeAnchorName => {
      if (port === 'port-top') return 'top';
      if (port === 'port-bottom') return 'bottom';
      if (port === 'port-left') return 'left';
      return 'right';
    };

    // 根据源节点与目标节点的相对位置，选择目标节点最近的边线中心端口
    const pickTargetPortConfig = (sourceNode: any, targetNode: any) => {
      const sBox = sourceNode.getBBox();
      const tBox = targetNode.getBBox();
      const sCx = sBox.x + sBox.width / 2;
      const sCy = sBox.y + sBox.height / 2;
      const tCx = tBox.x + tBox.width / 2;
      const tCy = tBox.y + tBox.height / 2;
      const dx = sCx - tCx;
      const dy = sCy - tCy;
      const port = Math.abs(dx) >= Math.abs(dy)
        ? (dx > 0 ? 'port-left' : 'port-right')
        : (dy > 0 ? 'port-top' : 'port-bottom');
      const anchorName = anchorForPort(port);
      return {
        port,
        anchor: { name: anchorName },
        connectionPoint: { name: 'anchor' },
      };
    };

    const pickStartDirection = (sourceNode: any, targetNode: any): EdgeAnchorName => {
      const sBox = sourceNode.getBBox();
      const tBox = targetNode.getBBox();
      const sCx = sBox.x + sBox.width / 2;
      const sCy = sBox.y + sBox.height / 2;
      const tCx = tBox.x + tBox.width / 2;
      const tCy = tBox.y + tBox.height / 2;
      const dx = tCx - sCx;
      const dy = tCy - sCy;
      if (Math.abs(dx) >= Math.abs(dy)) {
        return dx >= 0 ? 'right' : 'left';
      }
      return dy >= 0 ? 'bottom' : 'top';
    };

    const updateOptionArrowVisibility = (node: any) => {
      if (!node || !node.hasPort?.('port-arrow')) return;
      const hasArrowEdge = graph.getConnectedEdges(node).some((edge: any) => {
        const src = edge.getSource?.();
        const tgt = edge.getTarget?.();
        return (src?.port === 'port-arrow') || (tgt?.port === 'port-arrow');
      });
      const opacity = hasArrowEdge ? 0 : 1;
      node.setPortProp('port-arrow', 'attrs/arrowLine/opacity', opacity);
      node.setPortProp('port-arrow', 'attrs/arrowHead/opacity', opacity);
    };

    const toggleEdgeCssClass = (edge: Edge | null | undefined, className: string, active: boolean) => {
      if (!edge) return;
      const view = graph.findViewByCell(edge);
      if (!view?.container) return;
      if (active) {
        view.container.classList.add(className);
      } else {
        view.container.classList.remove(className);
      }
    };

    const clearSelectedEdge = () => {
      if (selectedEdgeRef.current) {
        toggleEdgeCssClass(selectedEdgeRef.current, 'edge-selected', false);
        selectedEdgeRef.current = null;
      }
    };

    const cleanGraphSelection = () => {
      graph.getNodes().forEach((n: any) => {
        n.setData({ selectionMode: undefined });
        applyNodeSelection(n, false);
      });
      clearSelectedEdge();
      selectionResultBoundsRef.current = null;
      if (selectionResultBoxRef.current) {
        selectionResultBoxRef.current.style.display = 'none';
      }
    };

    const selectGraphNode = (node: any, mode: 'single' | 'group' = 'single') => {
      node.setData({ selectionMode: mode });
      applyNodeSelection(node, true);
    };

    const getSelectedNodes = () =>
      graph.getNodes().filter((n: any) => n.getData()?.selected);

    const getNodesByIds = (ids: string[]) =>
      ids
        .map(id => graph.getCellById(id))
        .filter((cell: any) => cell?.isNode?.());

    const copySelectedNodes = () => {
      const selectedNodes = getSelectedNodes();
      if (!selectedNodes.length) return;

      const selectedIds = new Set(selectedNodes.map((n: any) => n.id));
      const topLevelNodes = selectedNodes.filter((node: any) => {
        const parent = node.getParent?.();
        return !parent || !selectedIds.has(parent.id);
      });

      const nodesToCopy: ClipboardNode[] = topLevelNodes.map((node: any) => {
        const data = { ...(node.getData() || {}) };
        delete data.selected;
        delete data.dragTarget;
        const children = (node.getChildren?.() || []).map((child: any) => {
          const childData = { ...(child.getData() || {}) };
          delete childData.selected;
          delete childData.dragTarget;
          return {
            id: child.id,
            shape: (child as any).shape,
            data: childData,
            position: child.getPosition(),
            size: child.getSize(),
          };
        });
        return {
          id: node.id,
          shape: (node as any).shape,
          data,
          position: node.getPosition(),
          size: node.getSize(),
          children,
        };
      });

      const nodeIdsToCopy = new Set<string>();
      nodesToCopy.forEach(node => {
        nodeIdsToCopy.add(node.id);
        node.children.forEach(child => nodeIdsToCopy.add(child.id));
      });

      const edgesToCopy: ClipboardEdge[] = graph
        .getEdges()
        .filter(edge => {
          const src = edge.getSourceCellId?.();
          const tgt = edge.getTargetCellId?.();
          return src && tgt && nodeIdsToCopy.has(src) && nodeIdsToCopy.has(tgt);
        })
        .map(edge => {
          const source = edge.getSource?.() as any;
          const target = edge.getTarget?.() as any;
          const rawData = { ...(edge.getData?.() || {}) };
          delete rawData._mergedEdge;
          return {
            id: edge.id,
            shape: (edge as any).shape,
            data: rawData,
            sourceId: edge.getSourceCellId?.() || '',
            targetId: edge.getTargetCellId?.() || '',
            sourcePort: source?.port,
            targetPort: target?.port,
            vertices: edge.getVertices?.() || undefined,
            router: edge.getRouter?.(),
            connector: edge.getConnector?.(),
            attrs: edge.getAttrs?.(),
          };
        });

      clipboardRef.current = { nodes: nodesToCopy, edges: edgesToCopy };
      pasteCountRef.current = 0;
      clipboardUsedRef.current = false;
      lastPastedNodeIdsRef.current = [];
      setStatusTextRef.current(`已复制 ${nodesToCopy.length} 个节点`);
    };

    const pasteClipboardNodes = () => {
      const clipboard = clipboardRef.current;
      if (!clipboard || !clipboard.nodes.length) return;
      if (clipboardUsedRef.current) {
        const nodesToHighlight = getNodesByIds(lastPastedNodeIdsRef.current);
        if (nodesToHighlight.length) {
          cleanGraphSelection();
          nodesToHighlight.forEach(node => selectGraphNode(node, 'group'));
          updateSelectionBoxFromNodes(nodesToHighlight);
        }
        message.info('本次复制只允许粘贴一次，已高亮上一次粘贴的位置');
        setStatusTextRef.current('已粘贴过一次，请重新复制后再粘贴');
        return;
      }
      pasteCountRef.current += 1;
      const newSelected: any[] = [];
      const idMap = new Map<string, string>();

      const allNodes = clipboard.nodes.flatMap(node => [node, ...node.children]);
      const minX = Math.min(...allNodes.map(n => n.position.x));
      const minY = Math.min(...allNodes.map(n => n.position.y));
      const anchor = lastCanvasClickRef.current;
      const deltaX = anchor ? anchor.x - minX : 30 * pasteCountRef.current;
      const deltaY = anchor ? anchor.y - minY : 30 * pasteCountRef.current;

      clipboard.nodes.forEach((item: ClipboardNode) => {
        const newNode = graph.addNode({
          shape: item.shape,
          x: item.position.x + deltaX,
          y: item.position.y + deltaY,
          width: item.size.width,
          height: item.size.height,
          data: { ...item.data, label: item.data.label },
        });
        idMap.set(item.id, newNode.id);
        newSelected.push(newNode);

        item.children.forEach((child: ClipboardNode['children'][number]) => {
          const relX = child.position.x - item.position.x;
          const relY = child.position.y - item.position.y;
          const newChild = graph.addNode({
            shape: child.shape,
            x: item.position.x + deltaX + relX,
            y: item.position.y + deltaY + relY,
            width: child.size.width,
            height: child.size.height,
            data: {
              ...child.data,
              label: child.data.label,
              parentId: newNode.id,
            },
          });
          newNode.embed(newChild);
          idMap.set(child.id, newChild.id);
          newSelected.push(newChild);
        });
      });

      clipboard.edges.forEach((edge: ClipboardEdge, idx: number) => {
        const sourceId = idMap.get(edge.sourceId);
        const targetId = idMap.get(edge.targetId);
        if (!sourceId || !targetId) return;
        const vertices = edge.vertices?.map(v => ({
          x: v.x + deltaX,
          y: v.y + deltaY,
        }));
        const source: { cell: string; port?: string } = { cell: sourceId };
        if (edge.sourcePort) source.port = edge.sourcePort;
        const target: { cell: string; port?: string } = { cell: targetId };
        if (edge.targetPort) target.port = edge.targetPort;
        graph.addEdge({
          id: `${edge.id}-copy-${Date.now()}-${idx}`,
          shape: edge.shape,
          source,
          target,
          data: { ...edge.data },
          attrs: edge.attrs,
          router: edge.router,
          connector: edge.connector,
          vertices,
        });
      });

      cleanGraphSelection();
      newSelected.forEach(node => selectGraphNode(node, 'group'));
      updateSelectionBoxFromNodes(newSelected);
      onNodeSelectRef.current?.(null);
      setStatusTextRef.current(`已粘贴 ${newSelected.length} 个节点`);
      applyParallelEdgeOffset(graph);
      mergeSameTypeEdges(graph);
      clipboardUsedRef.current = true;
      lastPastedNodeIdsRef.current = newSelected.map(node => node.id);
    };

    const ensureSelectionBox = () => {
      if (selectionBoxRef.current) return;
      const container = graph.container?.parentElement;
      if (!container) return;
      const box = document.createElement('div');
      box.className = 'opm-x6-selection-box';
      Object.assign(box.style, {
        position: 'absolute',
        border: '1px dashed #1890ff',
        background: 'rgba(24, 144, 255, 0.12)',
        display: 'none',
        pointerEvents: 'none',
        zIndex: 9999,
      });
      container.appendChild(box);
      selectionBoxRef.current = box;
    };

    const ensureSelectionResultBox = () => {
      if (selectionResultBoxRef.current) return;
      const container = graph.container?.parentElement;
      if (!container) return;
      const box = document.createElement('div');
      box.className = 'opm-x6-selection-box';
      Object.assign(box.style, {
        position: 'absolute',
        border: '2px dashed #1890ff',
        background: 'rgba(24, 144, 255, 0.05)',
        display: 'none',
        pointerEvents: 'none',
        zIndex: 9998,
      });
      container.appendChild(box);
      selectionResultBoxRef.current = box;
    };

    const hideSelectionBox = () => {
      if (selectionBoxRef.current) {
        selectionBoxRef.current.style.display = 'none';
      }
    };

    const updateSelectionBox = () => {
      if (!selectionBoxRef.current) return;
      const startClient = selectionStartClientRef.current;
      const currentClient = selectionCurrentClientRef.current;
      if (!startClient || !currentClient) return;
      const containerRect = graph.container.getBoundingClientRect();
      const left = Math.min(startClient.x, currentClient.x) - containerRect.left;
      const top = Math.min(startClient.y, currentClient.y) - containerRect.top;
      const width = Math.abs(currentClient.x - startClient.x);
      const height = Math.abs(currentClient.y - startClient.y);
      Object.assign(selectionBoxRef.current.style, {
        display: 'block',
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
      });
    };

    const resetSelectionState = () => {
      isRubberbandActiveRef.current = false;
      isRubberbandPreparingRef.current = false;
      selectionStartLocalRef.current = null;
      selectionCurrentLocalRef.current = null;
      selectionStartClientRef.current = null;
      selectionCurrentClientRef.current = null;
      hideSelectionBox();
    };

    const beginSelection = (clientX: number, clientY: number) => {
      selectionStartClientRef.current = { x: clientX, y: clientY };
      selectionCurrentClientRef.current = { x: clientX, y: clientY };
      selectionStartLocalRef.current = safePageToLocal(graph, clientX, clientY);
      selectionCurrentLocalRef.current = selectionStartLocalRef.current;
      isRubberbandPreparingRef.current = true;
      ensureSelectionBox();
      selectionResultBoundsRef.current = null;
      if (selectionResultBoxRef.current) {
        selectionResultBoxRef.current.style.display = 'none';
      }
      hideSelectionBox();
      graph.disablePanning();
    };

    const updateSelectionResultBox = () => {
      const bounds = selectionResultBoundsRef.current;
      if (!bounds) return;
      ensureSelectionResultBox();
      if (!selectionResultBoxRef.current) return;
      const containerRect = graph.container.getBoundingClientRect();
      const topLeft = graph.localToClient({ x: bounds.minX, y: bounds.minY });
      const bottomRight = graph.localToClient({
        x: bounds.maxX,
        y: bounds.maxY,
      });
      const left = topLeft.x - containerRect.left;
      const top = topLeft.y - containerRect.top;
      const width = bottomRight.x - topLeft.x;
      const height = bottomRight.y - topLeft.y;
      Object.assign(selectionResultBoxRef.current.style, {
        display: 'block',
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
      });
    };

    const updateSelectionBoxFromNodes = (nodes: any[]) => {
      if (!nodes.length) return;
      const bounds = nodes.reduce(
        (acc, node) => {
          const bbox = node.getBBox();
          return {
            minX: Math.min(acc.minX, bbox.x),
            minY: Math.min(acc.minY, bbox.y),
            maxX: Math.max(acc.maxX, bbox.x + bbox.width),
            maxY: Math.max(acc.maxY, bbox.y + bbox.height),
          };
        },
        { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
      );
      selectionResultBoundsRef.current = bounds;
      updateSelectionResultBox();
    };

    const showNodeControls = (node: any) => {
      node.attr({
        body: { strokeWidth: 3, stroke: '#1890ff' },
        portTop: { circle: { opacity: 1 } },
        portBottom: { circle: { opacity: 1 } },
        portLeft: { circle: { opacity: 1 } },
        portRight: { circle: { opacity: 1 } },
      });
    };

    const hideNodeControls = (node: any) => {
      node.attr({
        body: { strokeWidth: 2.5, stroke: typeStrokeColors[node.getData()?.type || 'object'] },
        portTop: { circle: { opacity: 0 } },
        portBottom: { circle: { opacity: 0 } },
        portLeft: { circle: { opacity: 0 } },
        portRight: { circle: { opacity: 0 } },
      });
    };

    const addEdgeTools = (edge: any) => {
      if (!edge.hasTool('vertices')) {
        edge.addTools([EDGE_VERTEX_TOOL, createEdgeRemoveTool()]);
      }
    };
    const removeEdgeTools = (edge: any) => {
      edge.removeTools();
    };

    const containerElement = graph.container;
    const updateCursor = (value: string) => {
      if (containerElement) containerElement.style.cursor = value;
    };
    graph.on('node:mouseenter', ({ node }) => {
      showNodeControls(node);
      updateCursor('move');
    });
    graph.on('node:mouseleave', ({ node }) => {
      hideNodeControls(node);
      updateCursor('default');
    });
    graph.on('node:port:mouseenter', () => updateCursor('crosshair'));
    graph.on('node:port:mouseleave', () => updateCursor('move'));

    // ---- finishDrag：查找光标下方的目标节点，弹出对话框或取消连线 ----
    const finishDrag = (localX?: number, localY?: number) => {
      const dragSourceNode = dragSourceNodeRef.current;
      const dragTempEdge = dragTempEdgeRef.current;
      const isDragging = isDraggingRef.current;
      const isMovingNode = isMovingNodeRef.current;
      const lastLocalPos = lastLocalPosRef.current;

      // 移动模式：清除关联边的手动顶点，重新施加平行边偏移
      if (isMovingNode) {
        if (groupDragNodesRef.current) {
          // 组拖拽结束：合并边、更新框选边界
          applyParallelEdgeOffset(graph);
          mergeSameTypeEdges(graph);
          // 更新框选结果边界
          if (selectionResultBoundsRef.current && groupDragNodesRef.current.length > 0) {
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            groupDragNodesRef.current.forEach(({ node: gNode }) => {
              const pos = gNode.getPosition();
              const size = gNode.getSize();
              minX = Math.min(minX, pos.x);
              minY = Math.min(minY, pos.y);
              maxX = Math.max(maxX, pos.x + size.width);
              maxY = Math.max(maxY, pos.y + size.height);
            });
            selectionResultBoundsRef.current = { minX, minY, maxX, maxY };
            updateSelectionResultBox();
          }
          groupDragNodesRef.current = null;
        } else if (dragSourceNodeRef.current) {
          // 重新计算平行边偏移，避免移动后边线再次重叠
          applyParallelEdgeOffset(graph);
          // 再次合并同类型边，保持主干展示
          mergeSameTypeEdges(graph);
        }
        dragSourceNodeRef.current = null;
        dragTempEdgeRef.current = null;
        isDraggingRef.current = false;
        isMovingNodeRef.current = false;
        setStatusTextRef.current('节点已移动');
        graph.enablePanning();  // 重新启用画布平移
        return;
      }

      if (!isDragging || !dragTempEdge || !dragSourceNode) {
        dragSourceNodeRef.current = null;
        dragTempEdgeRef.current = null;
        isDraggingRef.current = false;
        graph.enablePanning();  // 重新启用画布平移
        return;
      }

      const targetPos = (localX !== undefined && localY !== undefined)
        ? { x: localX, y: localY }
        : lastLocalPos;

      const targetNode = pickHoverTarget(targetPos);

      // 清除目标节点高亮
      if (highlightedTargetRef.current) {
        highlightedTargetRef.current.setData({ dragTarget: false });
        highlightedTargetRef.current = null;
      }

      const isFromArrowPort = dragSourcePortRef.current === 'port-arrow';

      if (targetNode && targetNode !== dragSourceNode) {
        if (isFromArrowPort) {
          // 属性节点箭头拖拽：直接创建边线，不弹出关系弹窗
          if (dragTempEdge && graph.hasCell(dragTempEdge.id)) {
            graph.removeCell(dragTempEdge.id);
          }
          const targetIsOption = targetNode.getData?.()?.type === 'option';
          const targetCfg: any = targetIsOption
            ? ensureConnectionPoint({ cell: targetNode.id, port: 'port-arrow' })
            : {
                cell: targetNode.id,
                port: 'port-top',
                anchor: { name: 'top' },
                connectionPoint: { name: 'anchor' },
              };
          const sourceCfg: any = { cell: dragSourceNode.id };
          if (dragSourcePortRef.current) sourceCfg.port = dragSourcePortRef.current;
          ensureConnectionPoint(sourceCfg);
          const structuralShape = getStructuralEdgeShape('aggregation-participation');
          const startDirection: EdgeAnchorName = 'top';
          const createdEdge = graph.addEdge({
            shape: structuralShape,
            source: sourceCfg,
            target: targetCfg,
            router: isFromArrowPort
              ? { name: 'normal' }
              : { name: 'orth', args: { padding: 20, startDirections: [startDirection] } },
            connector: isFromArrowPort ? 'normal' : { name: 'jumpover', args: { size: 5 } },
            data: {
              edgeCategory: 'structural',
              edgeSubtype: 'aggregation-participation',
              label: 'aggregation-participation',
              ...(isFromArrowPort ? { isOptionArrowEdge: true } : {}),
            },
            attrs: {
              line: {
                stroke: '#595959',
                strokeWidth: 1.8,
                targetMarker: {
                  name: 'path',
                  args: {
                    d: 'M 0 8 L 14 0 L 14 16 Z',
                    fill: 'none',
                    stroke: '#595959',
                    strokeWidth: 1.5,
                  },
                },
              },
            },
          });
          if (isFromArrowPort && createdEdge?.isEdge?.()) {
            applySingleEdgeRouting(graph, createdEdge, 'top', false, true);
          }
          applyParallelEdgeOffset(graph);
          mergeSameTypeEdges(graph);
          updateOptionArrowVisibility(dragSourceNode);
          updateOptionArrowVisibility(targetNode);
          setStatusTextRef.current(`已创建连线: ${dragSourceNode.getData()?.label || dragSourceNode.id} → ${targetNode.getData()?.label || targetNode.id}`);
        } else {
          const sourceLabel = dragSourceNode.getData()?.label || dragSourceNode.id;
          const targetLabel = targetNode.getData()?.label || targetNode.id;
          const sourceNodeType = dragSourceNode.getData()?.type || 'object';

          pendingEdgeRef.current = dragTempEdge;
          showEdgeDialogRef.current({
            edge: dragTempEdge,
            sourceId: dragSourceNode.id,
            targetId: targetNode.id,
            sourceLabel,
            targetLabel,
            sourceNodeType,
            sourcePort: dragSourcePortRef.current,
          });
          setStatusTextRef.current('请选择连线类型');
        }
      } else {
        if (dragTempEdge && graph.hasCell(dragTempEdge.id)) {
          graph.removeCell(dragTempEdge.id);
        }
        // 拖到空白处：恢复属性节点箭头可见性（临时边已删除，此时检查的是真实连线）
        updateOptionArrowVisibility(dragSourceNode);
        setStatusTextRef.current('未连接到目标节点，连线已取消');
      }

      dragSourceNodeRef.current = null;
      dragTempEdgeRef.current = null;
      isDraggingRef.current = false;
      dragSourcePortRef.current = undefined;
      graph.enablePanning();  // 重新启用画布平移
    };

    // ---- 节点事件 ----
    graph.on('node:mousedown', (args: any) => {
      const node = args.node;
      const e: MouseEvent | TouchEvent = args.e;
      if (e.ctrlKey || e.metaKey) return;

      // 阻止默认行为并停止传播，防止画布 panning 被触发
      e.preventDefault();
      e.stopPropagation();

      // 临时禁用画布平移，防止拖拽节点时触发画布移动
      graph.disablePanning();

      const target = (e.target || e.srcElement) as Element | null;
      const isTitleOrLabel = !!target && (
        target.classList?.contains('opm-node-title') ||
        target.classList?.contains('opm-node-label') ||
        !!target.closest?.('.opm-node-title, .opm-node-label')
      );
      const isPortHandle = !!target && !!target.closest?.('.opm-node-port');
      let sourcePortId: string | undefined;
      if (isPortHandle) {
        const portEl = target.closest('[port]') as Element | null;
        sourcePortId = portEl?.getAttribute('port') || undefined;
      }
      dragSourcePortRef.current = sourcePortId;
      const nodeType = node.getData()?.type;
      const isChildNode = nodeType === 'state' || nodeType === 'value';

      // 点击标题/标签或子状态时移动节点；点击空白区域创建连线
      const fromTextArea = !!(window as any).__OPM_TEXT_AREA_MOUSEDOWN;
      (window as any).__OPM_TEXT_AREA_MOUSEDOWN = false;
      const isOptionNode = nodeType === 'option';
      // 属性节点只能从箭头端口连线，空白区域点击进入移动模式
      const isMoveMode = !isPortHandle && (isTitleOrLabel || isChildNode || fromTextArea || isOptionNode);

      // 拖动子状态时移动子节点本身，在 mousemove 中限制在父节点边界内
      const sourceNode = node;

      const { clientX, clientY } = getEventClientXY(e);
      dragStartPosRef.current = { x: clientX, y: clientY };
      dragOriginalPosRef.current = { ...sourceNode.getPosition() };
      dragSourceNodeRef.current = sourceNode;
      isDraggingRef.current = false;
      isMovingNodeRef.current = isMoveMode;

      // 检查是否属于框选组，如果是则启动组拖拽
      const nodeData = node.getData();
      if (isMoveMode && nodeData?.selected && nodeData?.selectionMode === 'group') {
        const selectedNodes = getSelectedNodes().filter((n: any) => {
          const parent = n.getParent?.();
          return !parent;
        });
        if (selectedNodes.length > 1) {
          groupDragNodesRef.current = selectedNodes.map((n: any) => ({
            node: n,
            originalPos: { ...n.getPosition() },
          }));
        }
      }
    });

    // 使用画布级别的 mousemove 代替 node:mousemove，确保鼠标离开节点后仍能跟踪
    graph.on('blank:mousemove', handleNodeDragMove);
    graph.on('cell:mousemove', handleNodeDragMove);

    const pickHoverTarget = (point: { x: number; y: number }) => {
      const pad = 10;
      const candidates: Array<{ node: any; area: number; z: number; isParentCopy: boolean }> = [];
      graph.getNodes().forEach((n: any) => {
        if (n === dragSourceNodeRef.current) return;
        const bbox = n.getBBox();
        const within =
          point.x >= bbox.x - pad &&
          point.x <= bbox.x + bbox.width + pad &&
          point.y >= bbox.y - pad &&
          point.y <= bbox.y + bbox.height + pad;
        if (!within) return;
        const area = Math.max(1, bbox.width * bbox.height);
        const zIndex = typeof n.getZIndex === 'function' ? n.getZIndex() ?? 0 : 0;
        const data = n.getData?.() || {};
        candidates.push({
          node: n,
          area,
          z: zIndex,
          isParentCopy: !!data.isParentCopy,
        });
      });

      if (!candidates.length) return null;
      candidates.sort((a, b) => {
        if (a.isParentCopy !== b.isParentCopy) return a.isParentCopy ? 1 : -1;
        if (a.area !== b.area) return a.area - b.area;
        return b.z - a.z;
      });
      return candidates[0].node;
    };

    function handleNodeDragMove(args: any) {
      if (!graph) return;
      const node = dragSourceNodeRef.current;
      if (!node) return;
      const e: MouseEvent = args.e;
      if (e.ctrlKey || e.metaKey) return;

      const { clientX, clientY } = getEventClientXY(e);
      const localPoint = safePageToLocal(graph, clientX, clientY);
      lastLocalPosRef.current = { x: localPoint.x, y: localPoint.y };
      const dist = Math.hypot(clientX - dragStartPosRef.current.x, clientY - dragStartPosRef.current.y);

      if (!isDraggingRef.current && dist >= DRAG_THRESHOLD) {
        isDraggingRef.current = true;
        if (!isMovingNodeRef.current) {
          // 连线模式
          node.setPosition(dragOriginalPosRef.current);
          const sourceConfig: any = { cell: node.id };
          if (dragSourcePortRef.current) {
            sourceConfig.port = dragSourcePortRef.current;
            ensureConnectionPoint(sourceConfig);
          }
          const isFromArrow = dragSourcePortRef.current === 'port-arrow';
          if (isFromArrow && node.hasPort('port-arrow')) {
            node.setPortProp('port-arrow', 'attrs/arrowLine/opacity', 0);
            node.setPortProp('port-arrow', 'attrs/arrowHead/opacity', 0);
          }
          dragTempEdgeRef.current = graph.addEdge({
            shape: 'edge',
            source: sourceConfig,
            target: { x: localPoint.x, y: localPoint.y },
            router: isFromArrow
              ? { name: 'orth', args: { padding: 20 } }
              : { name: 'normal' },
            connector: { name: 'normal' },
            attrs: {
              line: {
                stroke: '#595959',
                strokeWidth: 1.8,
                targetMarker: isFromArrow
                  ? {
                      name: 'classic',
                      width: 12,
                      height: 12,
                      fill: '#595959',
                      stroke: '#595959',
                    }
                  : {
                      name: 'block',
                      width: 8,
                      height: 8,
                      fill: '#595959',
                      stroke: '#595959',
                    },
              },
            },
            zIndex: 9999,
          });
          setStatusTextRef.current('拖拽到目标节点松开以创建连线...');
        } else {
          setStatusTextRef.current(`移动节点: ${node.getData()?.label || node.id}`);
        }
      }

      if (isDraggingRef.current) {
        if (!isMovingNodeRef.current && dragTempEdgeRef.current) {
          node.setPosition(dragOriginalPosRef.current);
          dragTempEdgeRef.current.setTarget({ x: localPoint.x, y: localPoint.y });

          const hoverTarget = pickHoverTarget(localPoint);

          if (hoverTarget !== highlightedTargetRef.current) {
            if (highlightedTargetRef.current) highlightedTargetRef.current.setData({ dragTarget: false });
            if (hoverTarget) hoverTarget.setData({ dragTarget: true });
            highlightedTargetRef.current = hoverTarget;
          }
        } else if (isMovingNodeRef.current) {
          const dx = clientX - dragStartPosRef.current.x;
          const dy = clientY - dragStartPosRef.current.y;
          const zoom = graph.zoom();
          const targetX = dragOriginalPosRef.current.x + dx / zoom;
          const targetY = dragOriginalPosRef.current.y + dy / zoom;

          // 组拖拽：移动所有选中的顶层节点
          if (groupDragNodesRef.current && groupDragNodesRef.current.length > 1) {
            const deltaX = targetX - dragOriginalPosRef.current.x;
            const deltaY = targetY - dragOriginalPosRef.current.y;
            groupDragNodesRef.current.forEach(({ node: gNode, originalPos }) => {
              gNode.setPosition(originalPos.x + deltaX, originalPos.y + deltaY);
            });
            // 刷新所有相关边（mergeSameTypeEdges 会直接覆盖顶点，无需先清空）
            scheduleMerge();
            return;
          }

          const currentPos = node.getPosition();
          const moveDx = targetX - currentPos.x;
          const moveDy = targetY - currentPos.y;
          node.translate(moveDx, moveDy);
          const connected = graph.getConnectedEdges(node) as Edge[];
          const structuralEdges = connected.filter(isStructuralEdge);
          if (structuralEdges.length) {
            scheduleMerge();
          } else {
            connected.forEach(edge => edge.setVertices([]));
          }

          const parentNode = node.getParent();
          if (parentNode) {
            const parentType = parentNode.getData()?.type;
            const parentLabel = parentNode.getData()?.label || '';

            let minSize: { width: number; height: number };
            if (parentType === 'process') {
              minSize = calculateProcessSize(parentLabel);
            } else if (parentType === 'object' || parentType === 'option') {
              minSize = calculateObjectSize(parentLabel, false);
            } else if (parentType === 'state') {
              minSize = calculateStateSize(parentLabel);
            } else if (parentType === 'value') {
              minSize = calculateValueSize(parentLabel);
            } else {
              minSize = MIN_SIZES[parentType] || MIN_SIZES.object;
            }

            const padding = parentType === 'process'
              ? { top: 60, left: 50, right: 50, bottom: 60 }
              : { top: 50, left: 20, right: 20, bottom: 20 };

            parentNode.fit({ padding });

            const parentSize = parentNode.getSize();
            if (parentSize.width < minSize.width || parentSize.height < minSize.height) {
              const newWidth = Math.max(parentSize.width, minSize.width);
              const newHeight = Math.max(parentSize.height, minSize.height);
              parentNode.setSize({ width: newWidth, height: newHeight });
            }

            if (parentType === 'process') {
              const children = parentNode.getChildren()?.filter((c: any) => typeof c.isNode === 'function' && c.isNode()) || [];
              const pp = parentNode.getPosition();
              const ps = parentNode.getSize();
              const cx = pp.x + ps.width / 2;
              const cy = pp.y + ps.height / 2;
              let rx = ps.width / 2;
              let ry = ps.height / 2;
              const aspect = rx / ry;

              let needExpand = false;
              children.forEach((child: any) => {
                const cp = child.getPosition();
                const cs = child.getSize();
                const corners = [
                  { x: cp.x, y: cp.y },
                  { x: cp.x + cs.width, y: cp.y },
                  { x: cp.x, y: cp.y + cs.height },
                  { x: cp.x + cs.width, y: cp.y + cs.height },
                ];
                corners.forEach(({ x, y }) => {
                  const ddx = x - cx;
                  const ddy = y - cy;
                  const dist = (ddx * ddx) / (rx * rx) + (ddy * ddy) / (ry * ry);
                  if (dist > 1) {
                    needExpand = true;
                    const newRy = Math.sqrt((ddx * ddx) / (aspect * aspect) + ddy * ddy);
                    const newRx = aspect * newRy;
                    if (newRx > rx) { rx = newRx; ry = newRy; }
                  }
                });
              });

              if (needExpand) {
                parentNode.setPosition(cx - rx, cy - ry);
                parentNode.setSize({ width: rx * 2, height: ry * 2 });
              }
            }

            const newParentPos = parentNode.getPosition();
            const relX = node.getPosition().x - newParentPos.x;
            const relY = node.getPosition().y - newParentPos.y;
            const clamped = clampNodeToParent(node, parentNode, relX, relY);
            node.setPosition(newParentPos.x + clamped.x, newParentPos.y + clamped.y);
          }
        }
      }
    }

    // ---- 选中事件（单选/框选统一高亮） ----
    graph.on('node:selected', ({ node }) => applyNodeSelection(node, true));
    graph.on('node:unselected', ({ node }) => applyNodeSelection(node, false));

    // ---- 点击事件 ----
    graph.on('node:click', (args: any) => {
      const node = args.node;
      if (isDraggingRef.current) return;
      const { clientX, clientY } = getEventClientXY(args.e);
      lastCanvasClickRef.current = safePageToLocal(graph, clientX, clientY);
      if (suppressNodeClickRef.current) {
        suppressNodeClickRef.current = false;
        return;
      }

      cleanGraphSelection();
      selectGraphNode(node, 'single');
      onNodeSelectRef.current?.(node);
      setStatusTextRef.current(`选中: ${node.getData()?.label || node.id} (可拖拽移动)`);
    });

    // ---- 双击事件：同样选中节点（保留选中状态） ----
    graph.on('node:dblclick', (args: any) => {
      const node = args.node;
      const { clientX, clientY } = getEventClientXY(args.e);
      lastCanvasClickRef.current = safePageToLocal(graph, clientX, clientY);
      if (suppressNodeClickRef.current) {
        suppressNodeClickRef.current = false;
        return;
      }
      cleanGraphSelection();
      selectGraphNode(node, 'single');
      onNodeSelectRef.current?.(node);
      setStatusTextRef.current(`双击选中: ${node.getData()?.label || node.id}`);
    });

    graph.on('blank:click', (args: any) => {
      const { clientX, clientY } = getEventClientXY(args.e);
      lastCanvasClickRef.current = safePageToLocal(graph, clientX, clientY);
      if (suppressBlankClickRef.current) {
        suppressBlankClickRef.current = false;
        return;
      }
      cleanGraphSelection();
      onNodeSelectRef.current?.(null);
      setStatusTextRef.current('就绪');
    });

    graph.on('blank:mousedown', (args: any) => {
      const e: MouseEvent = args.e;
      if (e.altKey || e.button !== 0) return;
      lastCanvasClickRef.current = safePageToLocal(graph, e.clientX, e.clientY);
      beginSelection(e.clientX, e.clientY);
    });

    // ---- 鼠标/触摸抬起事件 ----
    graph.on('node:mouseup', (args: any) => {
      const { clientX, clientY } = getEventClientXY(args.e);
      const localPoint = safePageToLocal(graph, clientX, clientY);
      finishDrag(localPoint.x, localPoint.y);
    });

    graph.on('blank:mouseup', (args: any) => {
      const { clientX, clientY } = getEventClientXY(args.e);
      if (handleSelectionMouseUp(clientX, clientY)) return;
      const localPoint = safePageToLocal(graph, clientX, clientY);
      finishDrag(localPoint.x, localPoint.y);
    });

    // ---- 全局事件监听 ----
    const handleWindowMouseUp = (e: MouseEvent | TouchEvent) => {
      const { clientX, clientY } = getEventClientXY(e);
      if (handleSelectionMouseUp(clientX, clientY)) return;
      if (!isDraggingRef.current) return;
      const localPoint = safePageToLocal(graph, clientX, clientY);
      finishDrag(localPoint.x, localPoint.y);
    };
    window.addEventListener('mouseup', handleWindowMouseUp);
    window.addEventListener('touchend', handleWindowMouseUp);

    const handleSelectionMouseMove = (clientX: number, clientY: number) => {
      if (!isRubberbandPreparingRef.current && !isRubberbandActiveRef.current)
        return;
      selectionCurrentLocalRef.current = safePageToLocal(graph, clientX, clientY);
      selectionCurrentClientRef.current = { x: clientX, y: clientY };
      const startClient = selectionStartClientRef.current;
      if (!startClient) return;
      const dist = Math.hypot(clientX - startClient.x, clientY - startClient.y);
      if (!isRubberbandActiveRef.current && dist >= DRAG_THRESHOLD) {
        ensureSelectionBox();
        isRubberbandActiveRef.current = true;
      }
      if (isRubberbandActiveRef.current) {
        updateSelectionBox();
      }
    };

    const handleSelectionMouseUp = (clientX: number, clientY: number): boolean => {
      if (!isRubberbandActiveRef.current && !isRubberbandPreparingRef.current)
        return false;
      selectionCurrentLocalRef.current = safePageToLocal(graph, clientX, clientY);
      selectionCurrentClientRef.current = { x: clientX, y: clientY };
      if (isRubberbandActiveRef.current) {
        const start = selectionStartLocalRef.current;
        const current = selectionCurrentLocalRef.current;
        if (start && current) {
          const minX = Math.min(start.x, current.x);
          const maxX = Math.max(start.x, current.x);
          const minY = Math.min(start.y, current.y);
          const maxY = Math.max(start.y, current.y);
          const nodes = graph.getNodes();
          const selectedNodes = nodes.filter((node: any) => {
            const bbox = node.getBBox();
            const centerX = bbox.x + bbox.width / 2;
            const centerY = bbox.y + bbox.height / 2;
            return (
              centerX >= minX &&
              centerX <= maxX &&
              centerY >= minY &&
              centerY <= maxY
            );
          });
          cleanGraphSelection();
          if (selectedNodes.length) {
            selectedNodes.forEach(node => selectGraphNode(node, 'group'));
            selectionResultBoundsRef.current = {
              minX,
              minY,
              maxX,
              maxY,
            };
            updateSelectionResultBox();
            onNodeSelectRef.current?.(null);
            setStatusTextRef.current(`框选 ${selectedNodes.length} 个节点`);
          } else {
            onNodeSelectRef.current?.(null);
            setStatusTextRef.current('未选中节点');
          }
        }
        suppressBlankClickRef.current = true;
        suppressNodeClickRef.current = true;
      }
      resetSelectionState();
      graph.enablePanning();
      return true;
    };

    const handleGlobalMouseMove = (e: MouseEvent | TouchEvent) => {
      const { clientX, clientY } = getEventClientXY(e);
      handleSelectionMouseMove(clientX, clientY);
      if (isDraggingRef.current && dragTempEdgeRef.current) {
        const { clientX, clientY } = getEventClientXY(e);
        const localPoint = safePageToLocal(graph, clientX, clientY);
        lastLocalPosRef.current = { x: localPoint.x, y: localPoint.y };
        dragTempEdgeRef.current.setTarget({ x: localPoint.x, y: localPoint.y });

        if (dragSourceNodeRef.current) {
          dragSourceNodeRef.current.setPosition(dragOriginalPosRef.current);
        }

        const hoverTarget = pickHoverTarget(localPoint);

        if (hoverTarget !== highlightedTargetRef.current) {
          if (highlightedTargetRef.current) highlightedTargetRef.current.setData({ dragTarget: false });
          if (hoverTarget) hoverTarget.setData({ dragTarget: true });
          highlightedTargetRef.current = hoverTarget;
        }
      }
    };
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('touchmove', handleGlobalMouseMove, { passive: false });

    // 按 Escape 键取消拖拽
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        copySelectedNodes();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        pasteClipboardNodes();
        return;
      }
      if (e.key === 'Escape' && isDraggingRef.current && dragTempEdgeRef.current) {
        if (highlightedTargetRef.current) {
          highlightedTargetRef.current.setData({ dragTarget: false });
          highlightedTargetRef.current = null;
        }
        if (graph.hasCell(dragTempEdgeRef.current.id)) {
          graph.removeCell(dragTempEdgeRef.current.id);
        }
        if (dragSourceNodeRef.current) {
          dragSourceNodeRef.current.setPosition(dragOriginalPosRef.current);
          updateOptionArrowVisibility(dragSourceNodeRef.current);
        }
        dragSourceNodeRef.current = null;
        dragTempEdgeRef.current = null;
        isDraggingRef.current = false;
        dragSourcePortRef.current = undefined;
        setStatusTextRef.current('拖拽已取消');
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const activeEl = document.activeElement as HTMLElement | null;
        const isTypingTarget = !!activeEl &&
          (activeEl.tagName === 'INPUT' ||
            activeEl.tagName === 'TEXTAREA' ||
            activeEl.getAttribute('contenteditable') === 'true');
        if (isTypingTarget) return;

        const selectedNodes = getSelectedNodes();
        let removed = false;
        if (selectedNodes.length) {
          selectedNodes.forEach(node => node.remove());
          removed = true;
        }
        if (selectedEdgeRef.current) {
          selectedEdgeRef.current.remove();
          clearSelectedEdge();
          removed = true;
        }
        if (removed) {
          setStatusTextRef.current('已删除选中的元素');
        }
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    // ---- 连线悬停工具 ----
    const sendEdgeToBack = (edge?: Edge | null) => {
      if (!edge) return;
      // 临时拖拽边不发送到最底层，避免被父节点遮挡
      const z = (edge as any).getZIndex?.();
      if (z === 9999) return;
      edge?.toBack?.();
    };
    graph.getEdges().forEach(sendEdgeToBack);
    const handleEdgeAdded = ({ edge }: { edge: Edge }) => sendEdgeToBack(edge);
    graph.on('edge:added', handleEdgeAdded);

    const selectEdge = (edge: Edge | null) => {
      if (!edge) return;
      if (selectedEdgeRef.current?.id === edge.id) return;
      clearSelectedEdge();
      toggleEdgeCssClass(edge, 'edge-selected', true);
      selectedEdgeRef.current = edge;
    };

    graph.on('edge:mouseenter', ({ edge }: { edge: any }) => {
      if (edgeLeaveTimerRef.current !== null) {
        clearTimeout(edgeLeaveTimerRef.current);
        edgeLeaveTimerRef.current = null;
      }
      // 只高亮一条边：如果已经在 hover 另一条，先清除
      if (hoveredEdgeRef.current && hoveredEdgeRef.current.id !== edge.id) {
        toggleEdgeCssClass(hoveredEdgeRef.current, 'edge-hover', false);
        removeEdgeTools(hoveredEdgeRef.current);
      }
      // 如果当前已经 hover 的是同一条边，不重复操作
      if (hoveredEdgeRef.current?.id === edge.id) return;
      hoveredEdgeRef.current = edge;
      toggleEdgeCssClass(edge, 'edge-hover', true);
      addEdgeTools(edge);
      updateCursor('pointer');
    });
    graph.on('edge:mouseleave', ({ edge, e }: { edge: any; e: MouseEvent }) => {
      const related = e?.relatedTarget as HTMLElement | null;
      const withinTools = !!related && !!related.closest?.('.x6-widget-tools, .x6-edge-tools');
      if (withinTools) {
        return;
      }
      if (edgeLeaveTimerRef.current !== null) {
        clearTimeout(edgeLeaveTimerRef.current);
      }
      edgeLeaveTimerRef.current = window.setTimeout(() => {
        if (hoveredEdgeRef.current?.id === edge.id) {
          hoveredEdgeRef.current = null;
        }
        toggleEdgeCssClass(edge, 'edge-hover', false);
        removeEdgeTools(edge);
        updateCursor('default');
        edgeLeaveTimerRef.current = null;
      }, 120);
    });
    graph.on('edge:click', ({ edge, e }: { edge: Edge; e: MouseEvent }) => {
      e.stopPropagation();
      cleanGraphSelection();
      selectEdge(edge);
      setStatusTextRef.current(`选中连线: ${edge.getData()?.label || edge.id}`);
    });
    graph.on('edge:dblclick', ({ edge }: { edge: any }) => {
      setStatusTextRef.current(`边: ${edge.getData()?.label || edge.id}`);
    });

    // ---- 边线删除时恢复属性节点箭头 ----
    graph.on('edge:removed', ({ edge }: { edge: any }) => {
      const source = edge.getSource?.();
      const target = edge.getTarget?.();
      const srcPort = source?.port;
      const tgtPort = target?.port;
      const srcId = typeof source === 'string' ? source : source?.cell;
      const tgtId = typeof target === 'string' ? target : target?.cell;
      const srcCell = srcId ? graph.getCellById(srcId) : edge.getSourceCell?.();
      const tgtCell = tgtId ? graph.getCellById(tgtId) : edge.getTargetCell?.();
      if (srcPort === 'port-arrow' && srcCell) updateOptionArrowVisibility(srcCell);
      if (tgtPort === 'port-arrow' && tgtCell) updateOptionArrowVisibility(tgtCell);
      if (selectedEdgeRef.current?.id === edge.id) {
        selectedEdgeRef.current = null;
      }
    });

    const handleViewportChange = () => {
      updateSelectionResultBox();
    };
    graph.on('scale', handleViewportChange);
    graph.on('translate', handleViewportChange);

    // 更新状态栏，显示节点数量
    setStatusTextRef.current(
      `就绪 — ${graph.getNodes().length} 节点 | 点击节点空白处拖拽创建连线，点击标题/子状态可拖动节点，按住 Alt+左键拖动画布，左键框选可批量选中`
    );

    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp);
      window.removeEventListener('touchend', handleWindowMouseUp);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('touchmove', handleGlobalMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
      graph.off('edge:added', handleEdgeAdded);
      graph.off('scale', handleViewportChange);
      graph.off('translate', handleViewportChange);
      if (selectionBoxRef.current?.parentElement) {
        selectionBoxRef.current.parentElement.removeChild(selectionBoxRef.current);
      }
    };
  }, [graph]);

  return { pendingEdgeRef };
}
